import React from 'react';
import { cn } from '../../lib/utils';
import { motion, useReducedMotion } from 'motion/react';
import {
    FacebookIcon,
    InstagramIcon,
    LinkedinIcon,
    YoutubeIcon,
} from 'lucide-react';
import Button from './Button';

export function StickyFooter({ className, ...props }) {
    return (
        <footer
            className={cn('relative h-[720px] w-full bg-slate-950 text-white', className)}
            style={{ clipPath: 'polygon(0% 0, 100% 0%, 100% 100%, 0 100%)' }}
            {...props}
        >
            <div className="fixed bottom-0 h-[720px] w-full bg-slate-950">
                <div className="sticky top-[calc(100vh-720px)] h-full overflow-y-auto">
                    <div className="relative flex size-full flex-col justify-between gap-5 border-t border-white/10 px-4 pt-40 pb-8 md:px-12 md:pt-44">
                        <div
                            aria-hidden
                            className="absolute inset-0 isolate z-0 contain-strict"
                        >
                            <div className="bg-[radial-gradient(68.54%_68.72%_at_55.02%_31.46%,--theme(--color-foreground/.06)_0,hsla(0,0%,55%,.02)_50%,--theme(--color-foreground/.01)_80%)] absolute top-0 left-0 h-320 w-140 -translate-y-87.5 -rotate-45 rounded-full" />
                            <div className="bg-[radial-gradient(50%_50%_at_50%_50%,--theme(--color-foreground/.04)_0,--theme(--color-foreground/.01)_80%,transparent_100%)] absolute top-0 left-0 h-320 w-60 [translate:5%_-50%] -rotate-45 rounded-full" />
                            <div className="bg-[radial-gradient(50%_50%_at_50%_50%,--theme(--color-foreground/.04)_0,--theme(--color-foreground/.01)_80%,transparent_100%)] absolute top-0 left-0 h-320 w-60 -translate-y-87.5 -rotate-45 rounded-full" />
                        </div>
                        <div className="relative z-10 mt-10 flex flex-col gap-8 md:flex-row xl:mt-0">
                            <AnimatedContainer className="w-full max-w-sm min-w-2xs space-y-4">
                                <a href="/home-landing-page" className="flex items-center space-x-2 w-fit">
                                    <img
                                        src="https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/NESOL_2D_Logo_-_Black_and_White-removebg-preview.png"
                                        alt="Nesolearn Logo"
                                        className="w-8 h-8 object-contain invert"
                                    />
                                    <span className="text-lg font-bold text-white">Nesolearn</span>
                                </a>
                                <p className="text-white/60 mt-8 text-sm md:mt-0">
                                    The world's most valuable blockchain-powered learning
                                    intelligence platform. Nesolearn unites learners, educators, and
                                    institutions in one verifiable ecosystem for skills,
                                    credentials, and opportunity.
                                </p>
                                <div className="flex gap-2">
                                    {socialLinks.map((link) => (
                                        <Button key={link.title} size="icon" variant="outline" className="size-8 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white">
                                            <link.icon className="size-4" />
                                        </Button>
                                    ))}
                                </div>
                            </AnimatedContainer>
                            {footerLinkGroups.map((group, index) => (
                                <AnimatedContainer
                                    key={group.label}
                                    delay={0.1 + index * 0.1}
                                    className="w-full"
                                >
                                    <div className="mb-10 md:mb-0">
                                        <h3 className="text-sm uppercase text-white">{group.label}</h3>
                                        <ul className="text-white/60 mt-4 space-y-2  text-sm md:text-xs lg:text-sm">
                                            {group.links.map((link) => (
                                                <li key={link.title}>
                                                    {React.createElement('a', { href: link.href, className: 'hover:text-white inline-flex items-center transition-all duration-300' },
                                                        link.icon && <link.icon className="me-1 size-4" />,
                                                        link.title
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </AnimatedContainer>
                            ))}
                        </div>
                        <div className="relative z-10 text-white/60 flex flex-col items-center justify-between gap-2 border-t border-white/10 pt-2 text-sm md:flex-row">
                            <p>© {new Date().getFullYear()} Nesolearn. All rights reserved.</p>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}

const socialLinks = [
    { title: 'Facebook', href: '#', icon: FacebookIcon },
    { title: 'Instagram', href: '#', icon: InstagramIcon },
    { title: 'Youtube', href: '#', icon: YoutubeIcon },
    { title: 'LinkedIn', href: '#', icon: LinkedinIcon },
];

const footerLinkGroups = [
    {
        label: 'Product',
        links: [
            { title: 'Course Catalog', href: '/course-catalog' },
            { title: 'Learning Paths', href: '/course-catalog' },
            { title: 'Live Classes', href: '#' },
            { title: 'Mentorship', href: '#' },
            { title: 'Communities', href: '#' },
            { title: 'Skills Passport', href: '#' },
            { title: 'Certifications & Verification', href: '#' },
            { title: 'AI Learning Assistant', href: '#' },
            { title: 'Institutions & Course Builder', href: '/organization-onboarding' },
            { title: 'Analytics & Insights', href: '#' },
            { title: 'Dashboard', href: '/user-dashboard' },
            { title: 'Pricing', href: '#' },
        ],
    },
    {
        label: 'Solutions',
        links: [
            { title: 'For Individuals', href: '#' },
            { title: 'For Teams', href: '#' },
            { title: 'For Enterprises', href: '#' },
            { title: 'For Institutions', href: '/institutions-signup' },
            { title: 'For Startups', href: '#' },
            { title: 'Nesolearn ID', href: '#' },
            { title: 'Mentorship Marketplace', href: '#' },
            { title: 'Visioneer', href: '#' },
            { title: 'Government & Public Sector', href: '#' },
            { title: 'Nonprofits', href: '#' },
            { title: 'Corporate Training', href: '#' },
        ],
    },
    {
        label: 'Resources',
        links: [
            { title: 'Blog', href: '#' },
            { title: 'Case Studies', href: '#' },
            { title: 'Documentation', href: '#' },
            { title: 'API Reference', href: '#' },
            { title: 'Developer Tools', href: '#' },
            { title: 'Guides & Tutorials', href: '#' },
            { title: 'Whitepapers', href: '#' },
            { title: 'Reports & Research', href: '#' },
            { title: 'Events & Webinars', href: '#' },
            { title: 'E-books', href: '#' },
            { title: 'Community Forum', href: '#' },
            { title: 'Release Notes', href: '#' },
            { title: 'System Status', href: '#' },
        ],
    },
    {
        label: 'Company',
        links: [
            { title: 'About Us', href: '#' },
            { title: 'Leadership', href: '#' },
            { title: 'Careers', href: '#' },
            { title: 'Press', href: '#' },
            { title: 'Sustainability', href: '#' },
            { title: 'Diversity & Inclusion', href: '#' },
            { title: 'Investor Relations', href: '#' },
            { title: 'Partners', href: '#' },
            { title: 'Legal & Compliance', href: '#' },
            { title: 'Privacy Policy', href: '#' },
            { title: 'Cookie Policy', href: '#' },
            { title: 'Terms of Service', href: '#' },
            { title: 'AML & KYC Policy', href: '#' },
            { title: 'Regulatory Disclosures', href: '#' },
        ],
    },
];

function AnimatedContainer({ delay = 0.1, children, ...props }) {
    const shouldReduceMotion = useReducedMotion();

    if (shouldReduceMotion) {
        return children;
    }

    return (
        <motion.div
            initial={{ filter: 'blur(4px)', translateY: -8, opacity: 0 }}
            whileInView={{ filter: 'blur(0px)', translateY: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay, duration: 0.8 }}
            {...props}
        >
            {children}
        </motion.div>
    );
}

export default StickyFooter;
